import type { Money } from "@workspace/model"

/**
 * Presentation-only money formatting (US-018). `Money.amount` is minor units
 * (e.g. pence) — see the type doc in @workspace/model. This is display
 * formatting ONLY; it is not a pricing engine (that's packages/ordering,
 * US-024 — never compute authoritative totals here).
 */
export function formatMoney(money: Money, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: money.currency,
    }).format(money.amount / 100)
  } catch {
    // Unknown/invalid currency code — fall back to a plain, still-readable string
    // rather than throwing (never break the page over a formatting edge case).
    return `${(money.amount / 100).toFixed(2)} ${money.currency}`
  }
}

/** A modifier-option price delta: the caller's `zeroLabel` at 0, "+£1.50" above, "-£1.50" below. */
export function formatMoneyDelta(
  money: Money,
  locale: string,
  /**
   * What a ZERO delta renders as. Callers that want no copy at all for a
   * no-extra-charge option pass `""` (the meal-deal flow does — see
   * NO_PRICE_LABEL there); the caller decides whether to render the result.
   */
  zeroLabel: string
): string {
  if (money.amount === 0) return zeroLabel
  const formatted = formatMoney(money, locale)
  return money.amount > 0 ? `+${formatted}` : formatted
}

/**
 * Machine-readable decimal amount for structured data (US-023 JSON-LD
 * `Offer.price` — schema.org expects a plain decimal string, never a
 * currency-symbol-formatted string). Same minor-units convention as
 * {@link formatMoney}.
 */
export function moneyToDecimalString(money: Money): string {
  return (money.amount / 100).toFixed(2)
}
